import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  Divider,
  Chip,
  Fade,
  Button,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { getMeetingToken, generateMeetingToken, getMeetingNotes } from '../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const DISPATCH_DEPARTMENTS = [
  'AfterCare',
  'Installation',
  'Lighting & Shades',
  'IT',
  'Programming',
  'Project Managers',
  'CAD & Engineering',
];

const PROJECT_DETAIL_SECTIONS = [
  { key: 'optiportal', label: 'OptiPortal Reports' },
  { key: 'dropbox',   label: 'Dropbox Links' },
  { key: 'sage',      label: 'Sage Financials', mgmtOnly: true },
  { key: 'labor',     label: 'Labor Hours' },
];

const PROJECT_DETAIL_TABS = [
  { value: 'activity',       label: 'Activity' },
  { value: 'field_notes',    label: 'Field Notes' },
  { value: 'work_items',     label: 'Work Items' },
  { value: 'contacts',       label: 'Contacts' },
  { value: 'change_orders',  label: 'Change Orders' },
];

// ─── Saved indicator ──────────────────────────────────────────────────────────

const SavedIndicator = ({ show }) => (
  <Fade in={show}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
      <CheckCircleIcon fontSize="small" />
      <Typography variant="caption" fontWeight="medium">Saved</Typography>
    </Box>
  </Fade>
);

// ─── Section wrapper ──────────────────────────────────────────────────────────

const SettingsSection = ({ title, subtitle, saved, children }) => (
  <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
      <Box>
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <SavedIndicator show={saved} />
    </Box>
    <Divider sx={{ mb: 2.5 }} />
    {children}
  </Paper>
);

// ─── Meeting Notes Integration Section ───────────────────────────────────────

const MeetingIntegrationSection = () => {
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(false);

  useEffect(() => {
    getMeetingToken()
      .then(res => setTokenData(res.data))
      .catch(() => setTokenData({ token: null, webhook_url: null }))
      .finally(() => setLoading(false));

    setMeetingsLoading(true);
    getMeetingNotes()
      .then(res => setRecentMeetings((res.data || []).slice(0, 5)))
      .catch(() => setRecentMeetings([]))
      .finally(() => setMeetingsLoading(false));
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateMeetingToken();
      setTokenData(res.data);
    } catch (err) {
      console.error('Generate token error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (tokenData?.webhook_url) {
      navigator.clipboard.writeText(tokenData.webhook_url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const sourceLabel = (src) => {
    const map = { read_ai: 'Read.ai', fireflies: 'Fireflies', otter: 'Otter.ai', manual: 'Manual', unknown: 'Unknown' };
    return map[src] || src;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Meeting Notes Integration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Connect Read.ai, Fireflies.ai, or Otter.ai to automatically import meeting notes into projects.
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mb: 2.5 }} />

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        </Box>
      ) : tokenData?.webhook_url ? (
        <>
          <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
            Your Webhook URL
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Paste this URL into your Read.ai, Fireflies, or Otter.ai webhook settings.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              value={tokenData.webhook_url}
              size="small"
              fullWidth
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.78rem' } }}
            />
            <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
              <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'} size="small">
                {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Regenerate token (old URL will stop working)">
              <IconButton onClick={handleGenerate} disabled={generating} size="small">
                {generating ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          <Alert severity="info" sx={{ mb: 2.5, py: 0.5 }}>
            <Typography variant="caption">
              <strong>Read.ai:</strong> Settings → Integrations → Webhooks → Add Webhook<br />
              <strong>Fireflies:</strong> Integrations → Webhooks → Add Endpoint<br />
              <strong>Otter.ai:</strong> Settings → Integrations → Webhook URL
            </Typography>
          </Alert>
        </>
      ) : (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Generate a unique webhook URL to connect your meeting AI assistant.
          </Typography>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Generate Webhook URL
          </Button>
        </Box>
      )}

      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
        Recent Received Meetings
      </Typography>
      {meetingsLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        </Box>
      ) : recentMeetings.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No meetings imported yet. Once your webhook is configured, meetings will appear here.
        </Typography>
      ) : (
        <List dense disablePadding>
          {recentMeetings.map(m => (
            <ListItem key={m.id} disableGutters sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.75 }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="medium" sx={{ flex: 1 }} noWrap>
                      {m.meeting_title || 'Untitled Meeting'}
                    </Typography>
                    <Chip label={sourceLabel(m.source)} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {m.meeting_date ? new Date(m.meeting_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'}
                    {m.project_name ? ` · ${m.project_name}` : ' · Not linked to project'}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

// ─── Main Settings page ───────────────────────────────────────────────────────

const Settings = () => {
  const { preferences, updatePreference } = usePreferences();
  const { user } = useAuth();
  const isMgmt = user?.role === 'Management' || user?.roles?.includes('Management');

  // Track which sections last saved (for "Saved" flash)
  const [savedSection, setSavedSection] = useState(null);
  const saveTimers = useRef({});

  const flashSaved = useCallback((section) => {
    setSavedSection(section);
    if (saveTimers.current[section]) clearTimeout(saveTimers.current[section]);
    saveTimers.current[section] = setTimeout(() => {
      setSavedSection((s) => (s === section ? null : s));
    }, 2000);
  }, []);

  // ── Debounced save helper ────────────────────────────────────────────────────
  const debounceTimers = useRef({});

  const handleChange = useCallback((path, value, section) => {
    const timerKey = path;
    if (debounceTimers.current[timerKey]) clearTimeout(debounceTimers.current[timerKey]);
    debounceTimers.current[timerKey] = setTimeout(async () => {
      await updatePreference(path, value);
      flashSaved(section);
    }, 500);
    // Optimistic update already handled by updatePreference calling setPreferences internally,
    // but we call it immediately here so UI updates right away (no debounce on local state).
    updatePreference(path, value);
  }, [updatePreference, flashSaved]);

  // Special handler for immediate (non-debounced) updates used by checkboxes in lists
  const handleChangeImmediate = useCallback(async (path, value, section) => {
    await updatePreference(path, value);
    flashSaved(section);
  }, [updatePreference, flashSaved]);

  // ── Dashboard ────────────────────────────────────────────────────────────────
  const dash = preferences.dashboard || {};

  // ── Project Detail ───────────────────────────────────────────────────────────
  const pd = preferences.projectDetail || {};
  const hiddenSections = pd.hiddenSections || [];
  const isSectionHidden = (key) => hiddenSections.includes(key);

  const toggleSection = (key) => {
    const next = isSectionHidden(key)
      ? hiddenSections.filter((s) => s !== key)
      : [...hiddenSections, key];
    handleChangeImmediate('projectDetail.hiddenSections', next, 'projectDetail');
  };

  // ── Dispatch ─────────────────────────────────────────────────────────────────
  const dispatchPrefs = preferences.dispatch || {};
  const hiddenDepts = dispatchPrefs.hiddenDepartments || [];
  const isDeptHidden = (d) => hiddenDepts.includes(d);

  const toggleDept = (dept) => {
    const next = isDeptHidden(dept)
      ? hiddenDepts.filter((d) => d !== dept)
      : [...hiddenDepts, dept];
    handleChangeImmediate('dispatch.hiddenDepartments', next, 'dispatch');
  };

  // ── Display ──────────────────────────────────────────────────────────────────
  const display = preferences.display || {};

  // ── Notifications ────────────────────────────────────────────────────────────
  const notifs = preferences.notifications || {};

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize your view of the platform. Changes are saved automatically.
        </Typography>
      </Box>

      {/* ── Dashboard Settings ─────────────────────────────────────────────── */}
      <SettingsSection
        title="Dashboard"
        subtitle="Choose which panels appear on your dashboard."
        saved={savedSection === 'dashboard'}
      >
        <FormGroup sx={{ gap: 0.5 }}>
          {isMgmt && (
            <FormControlLabel
              control={
                <Switch
                  checked={dash.showFiscalYear !== false}
                  onChange={(e) => handleChange('dashboard.showFiscalYear', e.target.checked, 'dashboard')}
                  color="primary"
                />
              }
              label="Show Fiscal Year Overview"
            />
          )}
          {isMgmt && (
            <FormControlLabel
              control={
                <Switch
                  checked={dash.showCompanyLabor !== false}
                  onChange={(e) => handleChange('dashboard.showCompanyLabor', e.target.checked, 'dashboard')}
                  color="primary"
                />
              }
              label="Show Company Labor"
            />
          )}
          {isMgmt && (
            <FormControlLabel
              control={
                <Switch
                  checked={dash.showLaborUtilization !== false}
                  onChange={(e) => handleChange('dashboard.showLaborUtilization', e.target.checked, 'dashboard')}
                  color="primary"
                />
              }
              label="Show Labor Utilization"
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={dash.showProjectOverview !== false}
                onChange={(e) => handleChange('dashboard.showProjectOverview', e.target.checked, 'dashboard')}
                color="primary"
              />
            }
            label="Show Project Overview"
          />
        </FormGroup>

        <Box sx={{ mt: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Default Project View</InputLabel>
            <Select
              value={dash.defaultProjectView || 'kanban'}
              label="Default Project View"
              onChange={(e) => handleChange('dashboard.defaultProjectView', e.target.value, 'dashboard')}
            >
              <MenuItem value="kanban">Kanban</MenuItem>
              <MenuItem value="table">Table</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Projects Per Page</InputLabel>
            <Select
              value={dash.projectsPerPage || 25}
              label="Projects Per Page"
              onChange={(e) => handleChange('dashboard.projectsPerPage', e.target.value, 'dashboard')}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </SettingsSection>

      {/* ── Project Detail Settings ────────────────────────────────────────── */}
      <SettingsSection
        title="Project Detail"
        subtitle="Configure the default tab and which sections appear when viewing a project."
        saved={savedSection === 'projectDetail'}
      >
        <FormControl size="small" sx={{ minWidth: 220, mb: 3 }}>
          <InputLabel>Default Tab</InputLabel>
          <Select
            value={pd.defaultTab || 'activity'}
            label="Default Tab"
            onChange={(e) => handleChangeImmediate('projectDetail.defaultTab', e.target.value, 'projectDetail')}
          >
            {PROJECT_DETAIL_TABS.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
          Visible Sections
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Uncheck a section to hide it from project pages.
        </Typography>
        <FormGroup>
          {PROJECT_DETAIL_SECTIONS.map((s) => {
            if (s.mgmtOnly && !isMgmt) return null;
            return (
              <FormControlLabel
                key={s.key}
                control={
                  <Checkbox
                    checked={!isSectionHidden(s.key)}
                    onChange={() => toggleSection(s.key)}
                    color="primary"
                  />
                }
                label={s.label}
              />
            );
          })}
        </FormGroup>
      </SettingsSection>

      {/* ── Dispatch Calendar Settings ─────────────────────────────────────── */}
      <SettingsSection
        title="Dispatch Calendar"
        subtitle="Choose which departments are visible in the dispatch calendar by default."
        saved={savedSection === 'dispatch'}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Unchecked departments will be hidden when you first open Dispatch.
        </Typography>
        <FormGroup>
          {DISPATCH_DEPARTMENTS.map((dept) => (
            <FormControlLabel
              key={dept}
              control={
                <Checkbox
                  checked={!isDeptHidden(dept)}
                  onChange={() => toggleDept(dept)}
                  color="primary"
                />
              }
              label={dept}
            />
          ))}
        </FormGroup>
      </SettingsSection>

      {/* ── Display Settings ───────────────────────────────────────────────── */}
      <SettingsSection
        title="Display"
        subtitle="Adjust spacing and density across the app."
        saved={savedSection === 'display'}
      >
        <FormGroup sx={{ gap: 0.5, mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!display.compactMode}
                onChange={(e) => handleChange('display.compactMode', e.target.checked, 'display')}
                color="primary"
              />
            }
            label="Compact Mode (reduces padding and spacing)"
          />
        </FormGroup>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Table Row Density</InputLabel>
          <Select
            value={display.tableRowDensity || 'normal'}
            label="Table Row Density"
            onChange={(e) => handleChange('display.tableRowDensity', e.target.value, 'display')}
          >
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="compact">Compact</MenuItem>
            <MenuItem value="comfortable">Comfortable</MenuItem>
          </Select>
        </FormControl>
      </SettingsSection>

      {/* ── Notification Preferences ───────────────────────────────────────── */}
      <SettingsSection
        title="Notifications"
        subtitle="Control how and when you receive notifications. (More options coming soon)"
        saved={savedSection === 'notifications'}
      >
        <FormGroup sx={{ gap: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={notifs.emailDigest !== false}
                onChange={(e) => handleChange('notifications.emailDigest', e.target.checked, 'notifications')}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Email Digest
                <Chip label="Coming Soon" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={notifs.taskReminders !== false}
                onChange={(e) => handleChange('notifications.taskReminders', e.target.checked, 'notifications')}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Task Reminders
                <Chip label="Coming Soon" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              </Box>
            }
          />
        </FormGroup>
      </SettingsSection>

      {/* ── Meeting Notes Integration ──────────────────────────────────────── */}
      <MeetingIntegrationSection />
    </Box>
  );
};

export default Settings;
